/**
 * Plan — the week, with saved outfits placed on days.
 *
 * Rebuilt 12 Aug 2026. Brian: "on the planning page, also terrible formatting, terrible styling,
 * in that I've got a three quarter view of a card where the images are tiny, and I've got a one
 * quarter view right pane of outfits that are not clickable, that are not filterable, they're not
 * ordered, and I can simply just drag them onto the three quarter card. Planning the week, which
 * is supposed to be the easiest thing to do, is actually the hardest thing for me to do. Because
 * if I wanted to say pick Thursday's outfit, I don't know what to choose because there's no
 * images or filtering on the right hand pane."
 *
 * Every part of that was true. The right pane rendered all 30 saved outfits as names, in whatever
 * order the API returned, with no pictures — and 28 of the 30 are summer, so even the season gave
 * no help. What is fixed:
 *
 * - Pictures. OutfitThumb draws the actual garments, which is the only way one model-written
 *   outfit name is told from another at a glance.
 * - A season and occasion filter. The occasion filter only became worth having once migration
 *   0019 took occasions from ten to four; a ten-way filter over 30 rows is not a filter.
 * - Click to pick, then click a day. Drag still works and is still nice, but it was the ONLY
 *   path, and it does not exist on a phone — which is where this screen gets used, standing in
 *   a bedroom.
 * - Day cards show the outfit at a size you can actually read.
 */
import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import type {
  Outfit,
  OutfitPlanEntry,
  Season,
  TuckStyle,
  WardrobeOccasion,
} from '../../shared/types';
import OutfitThumb from './OutfitThumb';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  cx,
  useResource,
} from './ui';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SEASONS: Season[] = ['spring', 'summer', 'autumn', 'winter'];

/** Short form for the week grid — the full explanation lives on the Outfits tab. */
const TUCK_SHORT: Record<TuckStyle, string | null> = {
  tucked: 'Tuck it in',
  untucked: 'Leave it out',
  front_tuck: 'Front tuck',
  not_applicable: null,
};

/** Monday of the week containing `d`, as YYYY-MM-DD. */
function mondayOf(d: Date): string {
  const c = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = (c.getUTCDay() + 6) % 7; // Mon = 0
  c.setUTCDate(c.getUTCDate() - dow);
  return c.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

function prettyRange(weekStart: string): string {
  const end = addDays(weekStart, 6);
  const f = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };
  return `${f(weekStart)} – ${f(end)}`;
}

interface Bundle {
  plan: OutfitPlanEntry[];
  outfits: Outfit[];
  occasions: WardrobeOccasion[];
}

type SortKey = 'newest' | 'wearable' | 'name' | 'occasion';

export default function PlanPanel() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<string | null>(null);

  /** The click path: an outfit is armed, then a day accepts it. */
  const [picked, setPicked] = useState<number | null>(null);

  const [season, setSeason] = useState('all');
  const [occasion, setOccasion] = useState('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [wearableOnly, setWearableOnly] = useState(false);

  const todayIso = new Date().toISOString().slice(0, 10);

  const { data, loading, error, reload } = useResource<Bundle>(async () => {
    const [plan, outfits, occasions] = await Promise.all([
      api.get<OutfitPlanEntry[]>(`/wardrobe/plan?week_start=${weekStart}`),
      api.get<Outfit[]>('/wardrobe/outfits'),
      api.get<WardrobeOccasion[]>('/wardrobe/occasions'),
    ]);
    return { plan, outfits, occasions };
  }, [weekStart]);

  const byDate = useMemo(() => {
    const m = new Map<string, OutfitPlanEntry>();
    for (const p of data?.plan ?? []) m.set(p.plan_date, p);
    return m;
  }, [data]);

  const outfitById = useMemo(() => {
    const m = new Map<number, Outfit>();
    for (const o of data?.outfits ?? []) m.set(o.id, o);
    return m;
  }, [data]);

  const occasionLabel = useMemo(() => {
    const m = new Map((data?.occasions ?? []).map((o) => [o.slug, o.label]));
    return (slug: string | null) => (slug ? (m.get(slug) ?? slug.replace(/_/g, ' ')) : 'any');
  }, [data]);

  /** Only offer filters for values that actually occur — an empty result is not a choice. */
  const usedSeasons = useMemo(() => {
    const s = new Set<string>();
    for (const o of data?.outfits ?? []) for (const x of o.seasons) s.add(x);
    return SEASONS.filter((x) => s.has(x));
  }, [data]);

  const usedOccasions = useMemo(() => {
    const s = new Set<string>();
    for (const o of data?.outfits ?? []) if (o.occasion) s.add(o.occasion);
    return (data?.occasions ?? []).filter((o) => s.has(o.slug));
  }, [data]);

  const visible = useMemo(() => {
    const rows = (data?.outfits ?? []).filter((o) => {
      if (season !== 'all' && !o.seasons.includes(season as Season)) return false;
      if (occasion !== 'all' && (o.occasion ?? '') !== occasion) return false;
      if (wearableOnly && !o.wearable_today) return false;
      return true;
    });
    const cmp: Record<SortKey, (a: Outfit, b: Outfit) => number> = {
      /**
       * Default, and the reason it is default: Brian, 14 Aug 2026 — "saving outfit looks like
       * it works but then it is not available on the planning page." It was there. It had been
       * sorted wearable-first then alphabetically into the middle of twenty entries, three of
       * which are called "Black graphic tee, light denim". The thing you just made should be at
       * the top of the list you go to use it in.
       */
      newest: (a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id,
      // Things you can wear right now first, then by how little is missing.
      wearable: (a, b) =>
        Number(b.wearable_today) - Number(a.wearable_today) ||
        a.missing_count - b.missing_count ||
        a.name.localeCompare(b.name),
      name: (a, b) => a.name.localeCompare(b.name),
      occasion: (a, b) =>
        (a.occasion ?? 'zzz').localeCompare(b.occasion ?? 'zzz') || a.name.localeCompare(b.name),
    };
    return [...rows].sort(cmp[sort]);
  }, [data, season, occasion, wearableOnly, sort]);

  async function place(date: string, outfitId: number) {
    await api.put('/wardrobe/plan', { plan_date: date, outfit_id: outfitId }).catch(() => {});
    setPicked(null);
    reload();
  }

  async function clear(entryId: number) {
    await api.del(`/wardrobe/plan/${entryId}`).catch(() => {});
    reload();
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const pickedOutfit = picked !== null ? outfitById.get(picked) : null;
  const filtersOn = season !== 'all' || occasion !== 'all' || wearableOnly;

  return (
    <section className="space-y-5">
      <PanelHeading
        title="Plan the week"
        sub="Pick an outfit, then pick a day. Dragging works too."
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setWeekStart(addDays(weekStart, -7))}>&larr; Previous</Button>
        <span className="px-1 text-sm font-medium text-ink">{prettyRange(weekStart)}</span>
        <Button onClick={() => setWeekStart(addDays(weekStart, 7))}>Next &rarr;</Button>
        {weekStart !== mondayOf(new Date()) && (
          <Button variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))}>
            This week
          </Button>
        )}
      </div>

      {loading && <Loading label="Loading the week" rows={3} />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && data.outfits.length === 0 && (
        <EmptyState title="No saved outfits to plan with">
          Head to Outfits, suggest a few and keep the ones you like. They&rsquo;ll appear here to
          put on days.
        </EmptyState>
      )}

      {data && !loading && !error && data.outfits.length > 0 && (
        <>
          {/* The armed-outfit banner. Sticky because the picker is a long way from Thursday. */}
          {pickedOutfit && (
            <Card tone="ok" className="sticky top-2 z-10">
              <div className="flex flex-wrap items-center gap-3">
                <OutfitThumb outfit={pickedOutfit} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">{pickedOutfit.name}</p>
                  <p className="text-xs text-ink-soft">
                    Now click the day you want it on.
                  </p>
                </div>
                <Button variant="ghost" className="ml-auto" onClick={() => setPicked(null)}>
                  Cancel
                </Button>
              </div>
            </Card>
          )}

          <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
            {/* ---------------------------------------------------------------- the week */}
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {days.map((date, i) => {
                const entry = byDate.get(date);
                const outfit = entry?.outfit_id ? outfitById.get(entry.outfit_id) : null;
                const armed = pickedOutfit !== null;
                return (
                  <li key={date}>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setOver(date);
                      }}
                      onDragLeave={() => setOver((d) => (d === date ? null : d))}
                      onDrop={(e) => {
                        e.preventDefault();
                        setOver(null);
                        if (dragging !== null) void place(date, dragging);
                        setDragging(null);
                      }}
                      className={cx(
                        'rounded-card border p-3 transition-colors',
                        over === date
                          ? 'border-sage bg-sage-soft'
                          : armed
                            ? 'border-sage/50 bg-surface'
                            : date === todayIso
                              ? 'border-sage/40 bg-surface'
                              : 'border-border bg-surface'
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-ink">
                          {DAY_NAMES[i]}{' '}
                          <span className="font-normal text-ink-soft">{date.slice(8)}</span>
                          {date === todayIso && (
                            <span className="ml-2">
                              <Chip tone="ok">Today</Chip>
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {armed && (
                            <Button variant="primary" onClick={() => void place(date, picked!)}>
                              {outfit ? 'Replace' : 'Put it here'}
                            </Button>
                          )}
                          {entry && !armed && (
                            <Button variant="ghost" onClick={() => clear(entry.id)}>
                              Clear
                            </Button>
                          )}
                        </div>
                      </div>

                      {outfit ? (
                        <div className="mt-2 flex flex-wrap items-start gap-3">
                          <OutfitThumb outfit={outfit} size="md" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink">{outfit.name}</p>
                            <p className="mt-0.5 text-xs text-ink-soft">
                              {occasionLabel(outfit.occasion)}
                            </p>
                            {outfit.tuck && TUCK_SHORT[outfit.tuck] && (
                              <p className="mt-1 text-xs font-medium text-ink">
                                {TUCK_SHORT[outfit.tuck]}
                              </p>
                            )}
                            {!outfit.wearable_today && (
                              <p className="mt-1 text-xs text-clay">
                                Needs {outfit.missing_count} you don&rsquo;t own yet.
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2">
                          {/* Kept as well as the click path. It is the fastest route when you
                              know the outfit by name, and it is the keyboard-accessible one. */}
                          <Select
                            value=""
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (v) void place(date, v);
                            }}
                            aria-label={`Choose an outfit for ${DAY_NAMES[i]} ${date}`}
                          >
                            <option value="">Nothing planned</option>
                            {visible.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                                {o.wearable_today ? '' : ` (needs ${o.missing_count})`}
                              </option>
                            ))}
                          </Select>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* ---------------------------------------------------------------- the picker */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <h3 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
                  Outfits
                </h3>
                <span className="text-xs text-ink-soft">
                  {visible.length}
                  {filtersOn && ` of ${data.outfits.length}`}
                </span>
              </div>

              <div className="space-y-2">
                {usedSeasons.length > 1 && (
                  <label className="flex items-center gap-2 text-sm text-ink-soft">
                    <span className="w-16 shrink-0">Season</span>
                    <Select value={season} onChange={(e) => setSeason(e.target.value)}>
                      <option value="all">All</option>
                      {usedSeasons.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </label>
                )}
                {usedOccasions.length > 1 && (
                  <label className="flex items-center gap-2 text-sm text-ink-soft">
                    <span className="w-16 shrink-0">Occasion</span>
                    <Select value={occasion} onChange={(e) => setOccasion(e.target.value)}>
                      <option value="all">All</option>
                      {usedOccasions.map((o) => (
                        <option key={o.slug} value={o.slug}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm text-ink-soft">
                  <span className="w-16 shrink-0">Sort</span>
                  <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                    <option value="newest">Newest first</option>
                    <option value="wearable">Wearable first</option>
                    <option value="name">Name</option>
                    <option value="occasion">Occasion</option>
                  </Select>
                </label>
                <label className="flex items-center gap-2 text-sm text-ink-soft">
                  <input
                    type="checkbox"
                    checked={wearableOnly}
                    onChange={(e) => setWearableOnly(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-sage focus-visible:ring-2 focus-visible:ring-sage"
                  />
                  <span>Only what I can wear now</span>
                </label>
              </div>

              {visible.length === 0 ? (
                <EmptyState
                  title="Nothing matches"
                  action={
                    <Button
                      onClick={() => {
                        setSeason('all');
                        setOccasion('all');
                        setWearableOnly(false);
                      }}
                    >
                      Clear filters
                    </Button>
                  }
                >
                  {data.outfits.length} saved, none in this combination.
                </EmptyState>
              ) : (
                <ul className="space-y-2 lg:max-h-[42rem] lg:overflow-y-auto lg:pr-1">
                  {visible.map((o) => {
                    const isPicked = picked === o.id;
                    return (
                      <li
                        key={o.id}
                        draggable
                        onDragStart={() => setDragging(o.id)}
                        onDragEnd={() => {
                          setDragging(null);
                          setOver(null);
                        }}
                        className={cx(dragging === o.id && 'opacity-50')}
                      >
                        {/* A real button, so it is reachable by keyboard and reads as pressable.
                            The old pane was a bare <li draggable> — nothing announced it. */}
                        <button
                          type="button"
                          onClick={() => setPicked(isPicked ? null : o.id)}
                          aria-pressed={isPicked}
                          className={cx(
                            'w-full cursor-grab rounded-lg border p-2.5 text-left transition-colors',
                            'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-1',
                            'focus-visible:outline-none active:cursor-grabbing',
                            isPicked
                              ? 'border-sage bg-sage-soft'
                              : 'border-border bg-surface hover:bg-surface-sunk'
                          )}
                        >
                          <OutfitThumb outfit={o} />
                          <p className="mt-1.5 text-sm font-medium text-ink">{o.name}</p>
                          <p className="mt-0.5 text-xs text-ink-soft">
                            {occasionLabel(o.occasion)}
                            {o.seasons.length > 0 && ` · ${o.seasons.join('/')}`}
                          </p>
                          {!o.wearable_today && (
                            <p className="mt-0.5 text-xs text-clay">
                              needs {o.missing_count}
                            </p>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
